// Requests — where a person sees what became of the things they asked for: leave,
// a time correction, compensated hours. The three tables share one lifecycle
// (pending → approved | rejected), so they share one badge, one decision row and
// one pair of decision buttons.
//
// Inline object status, never an attention count: "yours is waiting" is not a
// queue anybody acts on from here (docs/UI-CONVENTIONS.md).
import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { Avatar, Badge, Button, Icon, Mono, TONE, type IconName } from './primitives';
import { PCard } from './people';
import { ConfirmDialog } from './dialog';
import { InlineStatus, useToast, type StatusTone } from './feedback';
import { supabase } from '../lib/supabase';
import { write } from '../lib/write';
import { reqRef } from '../lib/reqRef';
import { fmtDayKey, fmtRelative } from '../lib/dates';
import { useBelow } from '../lib/useMediaQuery';
import {
  correctionSummary,
  fmtDelta,
  type Correction,
  type PendingCorrection,
} from '../lib/timeCorrections';
import {
  fmtHours,
  type Category,
  type CompHours,
  type CompStatus,
  type PendingComp,
} from '../lib/compHours';

// Leave, correction and comp-hours requests share one lifecycle — CompStatus
// (compHours.ts) is the single source for it, not a second copy here.
type Status = CompStatus;

/** Which kind of request, so a waiting one can say who it is waiting for. */
export type RequestKind = 'leave' | 'correction' | 'comp' | 'day';

// When a request was filed or decided ("5 minutes ago"): words, so the UI face.
// It was mono, which is for figures, IDs and dates (critique 2026-10-06, mono
// carrying prose).
const stamp: CSSProperties = { fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' };

const STATUS = {
  pending: ['warning', 'waiting'],
  approved: ['success', 'approved'],
  rejected: ['danger', 'declined'],
  revoked: ['neutral', 'revoked'],
} as const;

// UI-CONVENTIONS Vocabulary: "waiting" alone tells the employee nothing to do.
// Leave and corrections are HR's; compensated hours are the manager's (HR only
// when there is no active manager, which the requester's own row can't see).
const WAITING_ON: Record<RequestKind, string> = {
  leave: 'Waiting for HR',
  correction: 'Waiting for HR',
  comp: 'Waiting for your manager',
  // Edit day (20261103120000): HR or the direct manager decides.
  day: 'Waiting for your manager or HR',
};

/** One badge for every request kind, so "declined" reads the same everywhere.
 *  With `kind`, a waiting request names who it waits on. Falls back to
 *  neutral/status rather than throwing on a status this map has not caught up
 *  with yet — a badge that says the wrong word is a smaller problem than a
 *  screen that will not render at all. */
export function RequestStatus({ status, kind }: { status: Status; kind?: RequestKind }) {
  const [tone, text] = STATUS[status] ?? (['neutral', status] as const);
  return (
    <Badge tone={tone} dot>
      {status === 'pending' && kind ? WAITING_ON[kind] : text}
    </Badge>
  );
}

// ── DecisionRow + DecisionActions ─────────────────────────────────────────────
// Leave, time corrections and compensated hours each drew their own decision row:
// three grids (190/190/180px), three verbs (Deny / Decline / denied) and three
// badge casings in one queue (HRA-09). One row and one pair of buttons now, in the
// UI-CONVENTIONS vocabulary: Approve / Decline, approved / declined. Decline asks
// for a reason where the table keeps one — declining was one click, with nothing
// said to the person whose request it was (HRA-10, ME-04).

/**
 * What kind of request a row is, as the approval queue's mockup draws it
 * (owner, 2026-10-09): an uppercase tag in the kind's colour, with its icon.
 */
const KIND: Record<RequestKind, { label: string; icon: IconName; tone: keyof typeof TONE }> = {
  leave: { label: 'Leave', icon: 'calendar', tone: 'sky' },
  correction: { label: 'Time correction', icon: 'flag', tone: 'lilac' },
  comp: { label: 'Comp hours', icon: 'clock', tone: 'amber' },
  // A whole-day change is a correction too: the same flag, the same colour.
  day: { label: 'Day change', icon: 'flag', tone: 'lilac' },
};
export function KindTag({ kind }: { kind: RequestKind }) {
  const k = KIND[kind];
  const t = TONE[k.tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--hf-space-1)',
        padding: '1px var(--hf-space-2)',
        borderRadius: 'var(--hf-radius-sm)',
        border: `1px solid ${t.bd}`,
        background: t.bg,
        color: t.fg,
        fontSize: 'var(--hf-text-2xs)',
        fontWeight: 'var(--hf-weight-medium)',
        letterSpacing: 'var(--hf-track-eyebrow)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name={k.icon} size={11} />
      {k.label}
    </span>
  );
}

/**
 * Approve, and Deny behind a confirm. `reason` is 'required' by default: all
 * three request tables keep a `decline_reason` (20260930130000). 'none' is for a
 * decline with nowhere to keep one, where the confirm only states what happens.
 */
export function DecisionActions({
  subject,
  onApprove,
  onDecline,
  reason = 'required',
  consequence,
  busy,
}: {
  /** What is being decided, e.g. "Asha Rao's casual leave". Titles the confirm. */
  subject: string;
  onApprove: () => void;
  onDecline: (reason?: string) => void;
  reason?: 'required' | 'none';
  /** What declining does. Defaults to the requester seeing it declined. */
  consequence?: ReactNode;
  busy?: boolean;
}) {
  const [declining, setDeclining] = useState(false);
  // The decision is the task on a phone, and a small Button is ~30px tall: each
  // gets a 44px hit area under a finger (.hf-hit, UI-CONVENTIONS §8). They sit
  // side by side, so only the height grows and the two areas never overlap.
  return (
    <span style={{ display: 'inline-flex', gap: 'var(--hf-space-2)' }}>
      <Button
        variant="ghost"
        size="sm"
        className="hf-hit"
        disabled={busy}
        onClick={() => setDeclining(true)}
      >
        Deny
      </Button>
      {/* Accent-outlined, as the mockup draws it: a wash and a line, not a fill. */}
      <Button
        variant="secondary"
        size="sm"
        className="hf-hit"
        icon="check"
        loading={busy}
        onClick={onApprove}
        style={{
          background: 'var(--hf-accent-wash)',
          borderColor: 'var(--hf-accent-line)',
          color: 'var(--hf-accent-text)',
        }}
      >
        Approve
      </Button>
      <ConfirmDialog
        open={declining}
        title={`Deny ${subject}?`}
        consequence={
          consequence ?? [
            reason === 'required'
              ? 'They see it declined, with your reason.'
              : 'They see it declined.',
            'Nothing is charged or credited.',
          ]
        }
        confirmLabel="Deny"
        requireReason={
          reason === 'required' ? { label: 'Reason — they will see this', minLength: 3 } : undefined
        }
        onCancel={() => setDeclining(false)}
        onConfirm={(why) => {
          setDeclining(false);
          onDecline(why);
        }}
      />
    </span>
  );
}

/**
 * One request in a decision queue: who, what and when on the left, the decision
 * (or its outcome) on the right. Flex-wrapped rather than fixed tracks, so at a
 * phone's width the actions drop under the request instead of squeezing it to
 * nothing (HRA-07).
 */
export function DecisionRow({
  name,
  reference,
  when,
  what,
  detail,
  filed,
  aside,
  flush,
}: {
  name: string;
  /** A short handle under the name, "REQ-1A2B" (the mockup's layout). */
  reference?: string;
  /** The day or range the request is about, already formatted. */
  when: string;
  /** The kind chip and figures ("Casual leave · 3 days"). */
  what: ReactNode;
  /** The requester's note. Wraps; it is not truncated behind a tooltip. */
  detail?: ReactNode;
  /** When it was filed (fmtRelative). */
  filed?: string;
  /** DecisionActions while it is pending, else its status. */
  aside: ReactNode;
  /** No side padding: the row sits in a container that already has its own. */
  flush?: boolean;
}) {
  const id = useId();
  return (
    <div
      role="group"
      aria-labelledby={id}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--hf-space-3) var(--hf-space-4)',
        padding: flush ? 'var(--hf-space-3) 0' : 'var(--hf-space-3) var(--hf-space-4)',
        borderBottom: '1px solid var(--hf-rule)',
      }}
    >
      <div
        style={{
          flex: '1 1 11rem',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-3)',
          minWidth: 0,
        }}
      >
        <Avatar name={name} size={32} />
        <div style={{ minWidth: 0 }}>
          <div
            id={id}
            style={{
              fontSize: 'var(--hf-text-sm)',
              fontWeight: 'var(--hf-weight-medium)',
              color: 'var(--hf-ink)',
              overflowWrap: 'anywhere',
            }}
          >
            {name}
          </div>
          <Mono dim>{reference ?? when}</Mono>
        </div>
      </div>
      <div style={{ flex: '2 1 14rem', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--hf-space-2)',
            marginBottom: detail ? 'var(--hf-space-1)' : 0,
          }}
        >
          {what}
        </div>
        {(detail || reference) && (
          <div
            style={{
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
              overflowWrap: 'anywhere',
            }}
          >
            {/* With a reference under the name, the day moves here, before the note. */}
            {reference && when}
            {reference && detail ? ' · ' : ''}
            {detail}
          </div>
        )}
      </div>
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: 'var(--hf-space-3)',
        }}
      >
        {filed && (
          <Mono dim style={stamp}>
            {filed}
          </Mono>
        )}
        {aside}
      </div>
    </div>
  );
}

// ── The two other queues, on the same row ────────────────────────────────────
// Time corrections and compensated-hours claims each had their own row: two more
// grids and two more one-click Declines with no reason (HRA-09, ME-04). They
// render through DecisionRow and DecisionActions now, and a decline writes
// `decline_reason`, which all three tables carry (20260930130000).
//
// Their outcome is said in an InlineStatus above the rows, not a toast: the
// correction queue also lives inside the amendment workspace, a modal dialog,
// where the toast host is inert. Neither decision can be undone — a decided
// correction has moved real hours, and a decided claim is final — so there is no
// Undo to offer.
type Outcome = { tone: StatusTone; text: string };

const decideRow = async (
  table: 'time_corrections' | 'compensated_hours',
  id: string,
  patch: Record<string, unknown>,
): Promise<string | null> => {
  if (!supabase) return 'Not connected.';
  const res = await write(supabase.from(table).update(patch).eq('id', id));
  if (res.ok) return null;
  return res.reason === 'refused' ? 'you can’t decide this request.' : res.error.message;
};

/** A reason the requester was given, where the row carries one. */
const declineReasonOf = (r: object): string | null =>
  (r as { decline_reason?: string | null }).decline_reason ?? null;

/** Where a decision would go on a row that is the viewer's own. */
export const YOURS: React.CSSProperties = {
  fontSize: 'var(--hf-text-xs)',
  color: 'var(--hf-ink-3)',
};

/**
 * Time corrections to decide, or already decided: the rows on their own, with no
 * card around them, so the approval queue can list them beside leave under one
 * set of filters.
 */
export function CorrectionDecisions({
  rows,
  uid,
  onChanged,
}: {
  rows: readonly PendingCorrection[];
  /** The approver. The trigger overwrites it with auth.uid() and refuses outright
   *  if the decider is the subject. */
  uid: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // Stays up to say how the last decision went, even when that emptied the
  // list: one waiting correction is the common case. Callers render this
  // whether or not there are rows.
  if (!rows.length && !outcome) return null;

  const decide = async (r: PendingCorrection, status: 'approved' | 'rejected', why?: string) => {
    setBusy(r.id);
    setOutcome(null);
    const what = `${r.full_name ?? 'Someone'}’s correction for ${fmtDayKey(r.work_date)}`;
    const error = await decideRow('time_corrections', r.id, {
      status,
      approver: uid,
      decided_at: new Date().toISOString(),
      ...(status === 'rejected' ? { decline_reason: why ?? null } : {}),
    });
    setOutcome(
      error
        ? { tone: 'error', text: `Couldn’t update ${what} — ${error}` }
        : { tone: 'success', text: `${status === 'approved' ? 'Approved' : 'Declined'} ${what}.` },
    );
    onChanged();
    setBusy(null);
  };

  return (
    <>
      {outcome && (
        <div style={{ margin: 'var(--hf-space-3) 0' }}>
          <InlineStatus tone={outcome.tone}>{outcome.text}</InlineStatus>
        </div>
      )}
      {rows.map((r) => {
        const why = declineReasonOf(r);
        return (
          <DecisionRow
            key={r.id}
            flush
            name={r.full_name ?? 'Someone'}
            reference={reqRef(r.id)}
            when={fmtDayKey(r.work_date, { weekday: true })}
            what={
              <>
                <KindTag kind="correction" />
                <Mono>{correctionSummary(r)}</Mono>
                <Mono dim>{fmtDelta(r.requested_seconds - r.current_seconds)}</Mono>
              </>
            }
            detail={
              <>
                {r.reason}
                {r.status === 'rejected' && why && (
                  <span style={{ display: 'block', color: 'var(--hf-ink-2)' }}>
                    Declined: {why}
                  </span>
                )}
              </>
            }
            filed={fmtRelative(r.created_at)}
            aside={
              r.status !== 'pending' ? (
                // No Revert, unlike leave: enforce_time_correction refuses to
                // re-decide outright ("an approved correction has already moved
                // real hours"), so the button would only ever offer an error.
                <RequestStatus status={r.status} />
              ) : r.profile_id === uid ? (
                // The database refuses this outright; saying so is better than
                // letting them find out by clicking.
                <span style={YOURS}>yours — another approver decides</span>
              ) : (
                <DecisionActions
                  subject={`${r.full_name ?? 'this'}${r.full_name ? '’s' : ''} time correction`}
                  busy={busy === r.id}
                  onApprove={() => void decide(r, 'approved')}
                  onDecline={(reason) => void decide(r, 'rejected', reason)}
                  consequence={[
                    'They see it declined, with your reason.',
                    'Their recorded hours for the day stay as they are.',
                  ]}
                />
              )
            }
          />
        );
      })}
    </>
  );
}

/**
 * A queue of compensated-hours claims to decide. Used twice with different
 * meanings: on My Space it is the manager's own reporting line, and on /hr it is
 * everything HR can see. `note` explains why HR's copy shows who the claim
 * actually belongs to.
 */
export function CompHoursDecisions({
  rows,
  uid,
  categories,
  title,
  note,
  onChanged,
  bare,
}: {
  rows: readonly PendingComp[];
  uid: string;
  categories: readonly Category[];
  title: string;
  note?: string;
  onChanged: () => void;
  /** The rows alone, inside the one approval queue (owner, 2026-10-09: "no
   *  need of separate approval queues"). The note says itself inline. */
  bare?: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // Stays up to say how the last decision went, even when that emptied the queue.
  if (!rows.length && !outcome) return null;

  const decide = async (r: PendingComp, status: 'approved' | 'rejected', why?: string) => {
    setBusy(r.id);
    setOutcome(null);
    const what = `${r.full_name ?? 'Someone'}’s ${fmtHours(r.hours)} for ${fmtDayKey(r.date)}`;
    const error = await decideRow('compensated_hours', r.id, {
      status,
      ...(status === 'rejected' ? { decline_reason: why ?? null } : {}),
    });
    setOutcome(
      error
        ? { tone: 'error', text: `Couldn’t update ${what} — ${error}` }
        : { tone: 'success', text: `${status === 'approved' ? 'Credited' : 'Declined'} ${what}.` },
    );
    onChanged();
    setBusy(null);
  };

  const body = (
    <>
      {note && !bare && (
        <p
          style={{
            margin: '0 0 var(--hf-space-2)',
            fontSize: 'var(--hf-text-sm)',
            color: 'var(--hf-ink-3)',
          }}
        >
          {note}
        </p>
      )}
      {outcome && (
        <div style={{ marginBottom: 'var(--hf-space-2)' }}>
          <InlineStatus tone={outcome.tone}>{outcome.text}</InlineStatus>
        </div>
      )}
      {rows.map((r) => (
        <DecisionRow
          key={r.id}
          flush
          name={r.full_name ?? 'Someone'}
          reference={reqRef(r.id)}
          when={fmtDayKey(r.date, { weekday: true })}
          what={
            <>
              <KindTag kind="comp" />
              {/* Title, then its figure, as the queue's other rows read. */}
              <span style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>
                {categories.find((c) => c.id === r.category)?.label ?? r.category}
              </span>
              <Mono>+{fmtHours(r.hours)}</Mono>
            </>
          }
          detail={r.note ?? undefined}
          filed={fmtRelative(r.created_at)}
          aside={
            r.status !== 'pending' ? (
              <RequestStatus status={r.status} />
            ) : r.profile_id === uid ? (
              <span style={YOURS}>yours — someone else decides</span>
            ) : (
              <DecisionActions
                subject={`${r.full_name ?? 'this'}${r.full_name ? '’s' : ''} compensated hours`}
                busy={busy === r.id}
                onApprove={() => void decide(r, 'approved')}
                onDecline={(reason) => void decide(r, 'rejected', reason)}
                consequence={[
                  'They see the claim declined, with your reason.',
                  'No hours are credited. They can file it again.',
                ]}
              />
            )
          }
        />
      ))}
    </>
  );
  return bare ? body : <PCard label={title}>{body}</PCard>;
}

export type MyLeave = {
  id: string;
  leave_type_id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  status: Status;
  created_at: string;
  decided_at: string | null;
  /** HR's reason, when a leave was declined (ME-04). Optional until My Space selects it. */
  decline_reason?: string | null;
};

/** A day or a range of days, "27 Sep" or "27 Sep – 29 Sep". Never UTC-parsed (HRA-V2). */
const dayRange = (from: string, to: string): string =>
  from === to ? fmtDayKey(from) : `${fmtDayKey(from)} – ${fmtDayKey(to)}`;

type Row = {
  key: string;
  id: string;
  kind: RequestKind;
  kindLabel: string;
  when: string;
  date: string;
  mono?: string;
  text: string;
  status: Status;
  decided: string | null;
  /** Why it was declined or revoked, where one was kept. */
  reason?: string | null;
};

// The table each kind lives in. Withdrawing is a delete: RLS lets the owner
// delete their own row, and for corrections and claims only while pending.
const TABLE: Record<RequestKind, string> = {
  leave: 'leave_requests',
  correction: 'time_corrections',
  comp: 'compensated_hours',
  day: 'day_edits',
};
const NOUN: Record<RequestKind, string> = {
  leave: 'leave request',
  correction: 'time correction',
  comp: 'compensated-hours claim',
  day: 'day change request',
};

/**
 * Take back one of your own pending requests (ME-03). Only while pending: the
 * `status = pending` filter makes a request HR decided a moment ago a zero-row
 * delete — a refusal — rather than deleting an approved leave out from under
 * its balance, which lr_delete alone would allow.
 */
async function withdrawRequest(kind: RequestKind, id: string) {
  if (!supabase) return { ok: false as const, reason: 'error' as const, message: 'Not connected.' };
  const r = await write(supabase.from(TABLE[kind]).delete().eq('id', id).eq('status', 'pending'));
  return r.ok
    ? r
    : r.reason === 'refused'
      ? { ...r, message: 'It has already been decided, so it can’t be withdrawn. Reload to see.' }
      : { ...r, message: r.error.message };
}

/**
 * Every request this person has filed, newest first. MeScreen loads the rows,
 * all three self-readable under RLS (lr_read, tc_read, ch_read); a pending one
 * can be withdrawn from here.
 */
export function MyRequests({
  leave,
  leaveTypes,
  corrections,
  comp,
  categories,
  onWithdrawn,
}: {
  leave: readonly MyLeave[];
  leaveTypes: readonly { id: string; label: string }[];
  corrections: readonly Correction[];
  comp: readonly CompHours[];
  categories: readonly Category[];
  /** After a withdrawal lands, so the caller can refetch. The row hides at once. */
  onWithdrawn?: (kind: RequestKind, id: string) => void;
}) {
  const toast = useToast();
  // Sets where a decline reason starts; see its marginLeft.
  const phone = useBelow('md');
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState<Row | null>(null);

  const rows: Row[] = [
    ...leave.map((r): Row => ({
      key: `l${r.id}`,
      id: r.id,
      kind: 'leave',
      kindLabel: 'Leave',
      when: r.created_at,
      date: dayRange(r.start_date, r.end_date),
      text: [leaveTypes.find((t) => t.id === r.leave_type_id)?.label ?? 'Leave', r.reason]
        .filter(Boolean)
        .join(' · '),
      status: r.status,
      decided: r.decided_at,
      reason: r.status === 'rejected' ? r.decline_reason : null,
    })),
    ...corrections.map((r): Row => ({
      key: `c${r.id}`,
      id: r.id,
      kind: 'correction',
      kindLabel: 'Time correction',
      when: r.created_at,
      date: fmtDayKey(r.work_date),
      mono: correctionSummary(r),
      text: r.reason,
      status: r.status,
      decided: r.decided_at,
      reason: r.status === 'rejected' ? r.decline_reason : null,
    })),
    ...comp.map((r): Row => ({
      key: `h${r.id}`,
      id: r.id,
      kind: 'comp',
      kindLabel: 'Compensated hours',
      when: r.created_at,
      date: fmtDayKey(r.date),
      mono: `+${fmtHours(r.hours)}`,
      text: categories.find((c) => c.id === r.category)?.label ?? r.category,
      status: r.status,
      decided: r.decided_at,
      reason:
        r.status === 'revoked'
          ? (r.revoke_reason ?? null)
          : r.status === 'rejected'
            ? (r.decline_reason ?? null)
            : null,
    })),
  ]
    .filter((r) => !gone.has(r.key))
    .sort((a, b) => b.when.localeCompare(a.when));

  const withdraw = async (r: Row) => {
    setConfirming(null);
    const res = await withdrawRequest(r.kind, r.id);
    if (!res.ok) {
      toast.push({ tone: 'error', message: `Couldn’t withdraw — ${res.message}` });
      return;
    }
    setGone((g) => new Set(g).add(r.key));
    toast.push({ tone: 'success', message: `Withdrew your ${NOUN[r.kind]} for ${r.date}.` });
    onWithdrawn?.(r.kind, r.id);
  };

  return (
    <PCard label="My requests">
      {rows.length === 0 ? (
        <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
          Nothing requested yet.
        </div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {rows.map((r) => (
            <div
              key={r.key}
              style={{ padding: 'var(--hf-space-2) 0', borderTop: '1px solid var(--hf-rule)' }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2) var(--hf-space-3)',
                  fontSize: 'var(--hf-text-sm)',
                }}
              >
                <span
                  style={{
                    width: '8rem',
                    flexShrink: 0,
                    fontSize: 'var(--hf-text-2xs)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--hf-track-eyebrow)',
                    color: 'var(--hf-ink-3)',
                  }}
                >
                  {r.kindLabel}
                </span>
                <span style={{ minWidth: '6rem', flexShrink: 0, color: 'var(--hf-ink)' }}>
                  {r.date}
                </span>
                {/* Wraps rather than truncating behind a hover title (ME-V3). */}
                <span
                  style={{
                    flex: '1 1 10rem',
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    color: 'var(--hf-ink-3)',
                  }}
                >
                  {r.mono && <Mono style={{ marginRight: 'var(--hf-space-2)' }}>{r.mono}</Mono>}
                  {r.text}
                </span>
                <RequestStatus status={r.status} kind={r.kind} />
                {r.decided && <span style={stamp}>{fmtRelative(r.decided)}</span>}
                {r.status === 'pending' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hf-hit"
                    onClick={() => setConfirming(r)}
                  >
                    Withdraw
                  </Button>
                )}
              </div>
              {/* Why, not just that — a declined leave or time correction, or a
                  revoked credit, is the status that needs an explanation next to it. */}
              {r.reason && (
                <div
                  style={{
                    marginTop: 'var(--hf-space-1)',
                    // Past the kind's 8rem column, under the date and text.
                    // Below md it is 0: on a phone the text usually wraps to
                    // the row's start, and the reason follows it. Keyed on
                    // width, not on where the text lands, so from about 480 to
                    // 768 the kind keeps its column and the reason starts under it.
                    marginLeft: phone ? 0 : 'calc(8rem + var(--hf-space-3))',
                    fontSize: 'var(--hf-text-xs)',
                    color: 'var(--hf-ink-3)',
                  }}
                >
                  {r.reason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={confirming !== null}
        title={`Withdraw this ${confirming ? NOUN[confirming.kind] : 'request'}?`}
        consequence={[
          `Your ${confirming ? NOUN[confirming.kind] : 'request'} for ${confirming?.date ?? ''} is deleted, and nobody is asked to decide it.`,
          'You can file it again later.',
        ]}
        confirmLabel="Withdraw request"
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && void withdraw(confirming)}
      />
    </PCard>
  );
}
